import React, { useState } from "react";

import {
  AUTH_COPY,
  AUTH_ERROR,
  MAXIMUM_PASSWORD_LENGTH,
} from "./authenticationConstants";
import "./treatmentAuthentication.css";

const ERROR_MESSAGE = {
  [AUTH_ERROR.credentialsInvalid]:
    "That email and password combination did not match. Please try again.",
  [AUTH_ERROR.authenticationUnavailable]:
    AUTH_COPY.errors.authenticationUnavailable,
  [AUTH_ERROR.rateLimited]:
    "Too many login attempts. Please wait before trying again.",
};

/** Password entry for an existing patient identity. */
export function ExistingPatientLoginStep({
  email,
  pending = false,
  error = null,
  errorMessage = null,
  onSubmit,
  onChangeEmail,
  onForgotPassword,
}) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState(null);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (pending) return;
    if (!password) {
      setLocalError(AUTH_COPY.errors.passwordRequired);
      return;
    }
    setLocalError(null);
    onSubmit({ email, password });
  };

  // Server errors are intentionally generic so the screen cannot be used to
  // work out whether an account exists.
  const message = localError || errorMessage || (error ? ERROR_MESSAGE[error] || null : null);

  return (
    <form className="treatment-auth" onSubmit={handleSubmit} noValidate>
      <p className="treatment-auth__eyebrow">{AUTH_COPY.loginStep.eyebrow}</p>
      <h1 className="treatment-auth__title">{AUTH_COPY.loginStep.title}</h1>
      <p className="treatment-auth__description">
        {AUTH_COPY.loginStep.description}
      </p>

      <label className="treatment-auth__label" htmlFor="intake-login-email">
        {AUTH_COPY.loginStep.emailLabel}
      </label>
      <input
        id="intake-login-email"
        type="email"
        className="treatment-auth__input treatment-auth__input--readonly"
        value={email}
        readOnly
        autoComplete="username"
      />

      <label className="treatment-auth__label" htmlFor="intake-password">
        {AUTH_COPY.loginStep.passwordLabel}
        <span className="treatment-auth__required" aria-hidden="true">
          *
        </span>
      </label>
      <div className="treatment-auth__password-field">
        <input
          id="intake-password"
          name="password"
          type={showPassword ? "text" : "password"}
          autoComplete="current-password"
          className={`treatment-auth__input${message ? " treatment-auth__input--error" : ""}`}
          placeholder={AUTH_COPY.loginStep.passwordPlaceholder}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          aria-invalid={Boolean(message)}
          aria-describedby={message ? "intake-password-error" : undefined}
          disabled={pending}
          maxLength={MAXIMUM_PASSWORD_LENGTH}
        />
        <button
          type="button"
          className="treatment-auth__password-toggle"
          onClick={() => setShowPassword((prev) => !prev)}
          disabled={pending}
          aria-label={showPassword ? "Hide password" : "Show password"}
          aria-pressed={showPassword}
        >
          {showPassword ? (
            <svg
              className="treatment-auth__eye-icon"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
          ) : (
            <svg
              className="treatment-auth__eye-icon"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          )}
        </button>
      </div>
      {message && (
        <p
          className="treatment-auth__error"
          id="intake-password-error"
          role="alert"
        >
          {message}
        </p>
      )}

      <button
        type="submit"
        className="treatment-auth__submit"
        disabled={pending}
      >
        {pending ? AUTH_COPY.loginStep.submitting : AUTH_COPY.loginStep.submit}
      </button>

      <p className="treatment-auth__footnote">
        {AUTH_COPY.loginStep.changeEmailPrompt}{" "}
        <button
          type="button"
        className="treatment-auth__link treatment-auth__link--strong"
        onClick={onChangeEmail}
        disabled={pending}
        >
          {AUTH_COPY.loginStep.changeEmail}
        </button>
      </p>
      {onForgotPassword && (
        <p className="treatment-auth__footnote">
          <button
            type="button"
            className="treatment-auth__link"
            onClick={onForgotPassword}
            disabled={pending}
          >
            {AUTH_COPY.loginStep.forgotPassword}
          </button>
        </p>
      )}
    </form>
  );
}

export default ExistingPatientLoginStep;
